import { useId, type ReactNode } from 'react';
import { clsx } from 'clsx';

/**
 * The shell every form control sits in: label, hint, error, and the ids that tie
 * them together. Written once so a validation message is announced the same way on
 * every screen, and so no control ever ships without a label.
 */

export interface FieldProps {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  /** Hides the label visually but keeps it for screen readers. */
  hideLabel?: boolean;
  className?: string;
  children: (ids: { id: string; describedBy: string | undefined; invalid: boolean }) => ReactNode;
}

export function Field({
  label,
  hint,
  error,
  required = false,
  hideLabel = false,
  className,
  children,
}: FieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy =
    [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(' ') || undefined;

  return (
    <div className={clsx('flex min-w-0 flex-col gap-1.5', className)}>
      {label ? (
        <label
          htmlFor={id}
          className={clsx(
            'text-micro font-medium text-ink-2',
            hideLabel && 'sr-only',
          )}
        >
          {label}
          {required ? (
            <span className="ml-0.5 text-danger" aria-hidden>
              *
            </span>
          ) : null}
        </label>
      ) : null}

      {children({ id, describedBy, invalid: Boolean(error) })}

      {error ? (
        <p id={errorId} className="text-micro text-danger">
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="text-micro text-muted">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

/** The shared look of an input, select or combobox trigger. */
export function controlClass(opts: {
  size?: 'sm' | 'md';
  invalid?: boolean;
  disabled?: boolean;
  hasLeadingIcon?: boolean;
}): string {
  const { size = 'md', invalid = false, disabled = false, hasLeadingIcon = false } = opts;
  return clsx(
    'tap-safe w-full min-w-0 rounded-control border bg-surface text-input text-ink placeholder:text-faint',
    'transition-[border-color,box-shadow] duration-150',
    'focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-accent',
    size === 'sm' ? 'h-8 text-micro' : 'h-9 text-body',
    hasLeadingIcon ? (size === 'sm' ? 'pl-8 pr-2.5' : 'pl-9 pr-3') : 'px-2.5',
    invalid ? 'border-danger' : 'border-line-strong hover:border-faint',
    disabled && 'cursor-not-allowed bg-surface-2 text-muted',
  );
}
